import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBookingsStaffSettingsV1StaffMemberSettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/staff-member-settings',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/bookings/v1/staff-member-settings',
        destPath: '/v1/staff-member-settings',
      },
    ],
    _: [
      {
        srcPath: '/_api/staff-member-settings',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/staff-member-settings',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_staff-member-settings';

/**
 * Retrieves the current privacy settings for staff member contact information.
 *
 * Use this method to check whether the `email` and `phone` fields in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) are accessible in API responses and on the live site.
 */
export function getStaffMemberSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __getStaffMemberSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff_settings.v1.staff_member_settings',
      method: 'GET' as any,
      methodFqn:
        'wix.bookings.staff.settings.v1.StaffMemberSettingsService.GetStaffMemberSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffSettingsV1StaffMemberSettingsServiceUrl({
        protoPath: '/v1/staff-member-settings',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMemberSettings.createdDate' },
              { path: 'staffMemberSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getStaffMemberSettings;
}

/**
 * Updates privacy settings for staff member contact information.
 *
 * Each time the settings are updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the settings.
 * This ensures you're working with the latest settings and prevents unintended overwrites.
 *
 * Changes to these settings take effect immediately and affect both API responses and live site display.
 * When you enable `contactInformation.publiclyAccessible`, the `email` and `phone` fields in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) include actual contact information, and this information is displayed on the live site.
 * When you disable it, these fields are empty in API responses if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions, and contact information isn't displayed on the live site.
 *
 * Identities with appropriate permissions can access staff contact information in API responses regardless of this setting.
 */
export function updateStaffMemberSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __updateStaffMemberSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'staffMemberSettings.createdDate' },
          { path: 'staffMemberSettings.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.staff_settings.v1.staff_member_settings',
      method: 'PATCH' as any,
      methodFqn:
        'wix.bookings.staff.settings.v1.StaffMemberSettingsService.UpdateStaffMemberSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffSettingsV1StaffMemberSettingsServiceUrl({
        protoPath: '/v1/staff-member-settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMemberSettings.createdDate' },
              { path: 'staffMemberSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateStaffMemberSettings;
}
